
import { inject, Injectable, signal } from '@angular/core';
import { DownloadJob, Highlight } from '../models/twitch.model';
import { FirebaseService } from './firebase.service.js';
import { TwitchChatStateService } from './twitch-chat-state.service.js';
import { DemoService } from './demo.service.js';
import { QuotaExceededError, QuotaService } from './quota.service.js';
import { collection, doc, onSnapshot } from 'firebase/firestore';

@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    // Queue state for UI tracking
    readonly downloadQueue = signal<DownloadJob[]>([]);
    readonly isBatchDownloading = signal<boolean>(false); // New signal for global batch state
    readonly batchDownloadProgress = signal<number>(0);
    readonly batchDownloadQueued = signal<boolean>(false); // True when waiting for a free server

    // Recently-completed clip URLs (signed, GCS) keyed by highlight.startTime.
    // Used as a popup-blocker fallback: if the auto-fired download was blocked
    // (e.g. browser saw the click as async-after-fetch and treated it as popup),
    // user can hit the same «Скачать» button — the second click is a fresh user
    // gesture AND we re-use the existing URL instead of regenerating the clip
    // on the backend. Entries expire 50 min after creation (GCS signs clips
    // for 60 min, so we leave a 10-min safety margin).
    private readonly recentDownloads = signal<Map<string, { url: string; filename: string; expiresAt: number }>>(new Map());
    private readonly RECENT_DOWNLOAD_TTL_MS = 50 * 60 * 1000;

    /**
     * Cache key for recentDownloads. Range-based so that moving in/out borders
     * on a user-clip (or any other highlight) invalidates the cache and forces
     * a fresh worker job — otherwise a stale signed URL from the previous range
     * would be re-fired by getRecentDownload.
     */
    private cacheKeyFor(highlight: Highlight, videoId: string | number): string {
        return `vod:${videoId}:${Math.floor(highlight.clipStartTime)}:${Math.ceil(highlight.clipEndTime)}`;
    }

    // Client-side concurrency limit for single-clip downloads. Worker has
    // containerConcurrency=1 + maxScale=5 = max 5 parallel; we cap at 3 to leave
    // headroom for cold starts and mixed workload (e.g. one user batches while
    // another opens a single clip). Excess clicks queue locally — UI surfaces
    // them as 'pending' jobs, see downloadQueue.
    private static readonly MAX_CONCURRENT_CLIP_DOWNLOADS = 3;
    private activeClipDownloads = 0;
    private clipDownloadWaiters: Array<() => void> = [];

    private readonly firebaseService = inject(FirebaseService);
    private readonly state = inject(TwitchChatStateService);
    private readonly demoService = inject(DemoService);
    private readonly quotaService = inject(QuotaService);

    /** Reserve a slot in the semaphore. Resolves immediately if free, otherwise queues. */
    private async acquireClipSlot(): Promise<void> {
        if (this.activeClipDownloads < DownloadService.MAX_CONCURRENT_CLIP_DOWNLOADS) {
            this.activeClipDownloads++;
            return;
        }
        return new Promise<void>(resolve => {
            this.clipDownloadWaiters.push(() => {
                this.activeClipDownloads++;
                resolve();
            });
        });
    }

    /** Release the slot and wake up the next waiter, if any. */
    private releaseClipSlot(): void {
        this.activeClipDownloads--;
        const next = this.clipDownloadWaiters.shift();
        if (next) next();
    }

    // Sequential queue for browser <a>.click() triggers. Cause: when 3 clips
    // finish on the worker within ~1 second of each other, Chrome's
    // "site is downloading multiple files" protection silently cancels some
    // of them — the network tab shows the GCS URLs as `(canceled)` with no
    // error in Console. Forcing a 1200ms gap between trigger calls gives
    // each download time to register before the next one starts, which
    // bypasses the detection. Pure async, never blocks the network fetch
    // from the worker — only the final browser-side save dispatch.
    private downloadTriggerQueue: Array<() => Promise<void>> = [];
    private isProcessingTriggerQueue = false;
    private static readonly DOWNLOAD_TRIGGER_GAP_MS = 1200;

    private enqueueBrowserDownload(url: string, filename: string): Promise<void> {
        return new Promise<void>(resolve => {
            this.downloadTriggerQueue.push(async () => {
                this.triggerBrowserDownload(url, filename);
                await new Promise(r => setTimeout(r, DownloadService.DOWNLOAD_TRIGGER_GAP_MS));
                resolve();
            });
            this.processTriggerQueue();
        });
    }

    private async processTriggerQueue(): Promise<void> {
        if (this.isProcessingTriggerQueue) return;
        this.isProcessingTriggerQueue = true;
        while (this.downloadTriggerQueue.length > 0) {
            const next = this.downloadTriggerQueue.shift()!;
            await next();
        }
        this.isProcessingTriggerQueue = false;
    }

    isHighlightInQueue(startTime: number): boolean {
        return this.downloadQueue().some(job => job.highlight.startTime === startTime && (job.status === 'pending' || job.status === 'processing'));
    }

    /**
     * Returns a still-valid signed URL for a previously-downloaded clip, or null
     * if no fresh entry exists. Caller should invoke retriggerDownload() with the
     * returned data to bypass the backend on a re-click.
     */
    getRecentDownload(highlight: Highlight, videoId: string | number): { url: string; filename: string } | null {
        const key = this.cacheKeyFor(highlight, videoId);
        const entry = this.recentDownloads().get(key);
        if (!entry) return null;
        if (Date.now() > entry.expiresAt) {
            this.recentDownloads.update(m => {
                const next = new Map(m);
                next.delete(key);
                return next;
            });
            return null;
        }
        return { url: entry.url, filename: entry.filename };
    }

    /**
     * Re-fire the browser download for a cached URL. Synchronous wrt the click
     * event, so popup blockers treat it as user-initiated.
     */
    retriggerDownload(url: string, filename: string): void {
        this.triggerBrowserDownload(url, filename);
    }

    async downloadClip(highlight: Highlight): Promise<void> {
        // Legacy wrapper
        alert('Please use downloadClipWithId');
    }

    async downloadClipWithId(highlight: Highlight, videoId: string | number): Promise<void> {
        if (this.demoService.isDemoMode()) {
            this.demoService.requestSignIn('download-clip');
            return;
        }

        // Daily-quota pre-check (fast path before semaphore + worker round-trip).
        // Server enforces the same check — this is just a UX shortcut so we don't
        // spawn a queued 'pending' job that we know will 429.
        const cq = this.quotaService.clipQuota();
        if (cq && cq.exhausted) {
            throw new QuotaExceededError(
                'clip',
                cq.limit ?? 0,
                this.quotaService.resetAt()?.toISOString() || null,
            );
        }

        const cloudRunUrl = this.firebaseService.cloudRunWorkerUrl;
        if (!cloudRunUrl) throw new Error('Cloud Run URL missing');

        const cloudHeaders = await this.firebaseService.buildCloudRunHeaders();
        if (!cloudHeaders) {
            alert('Сначала войдите в аккаунт');
            return;
        }

        const jobId = `job-${highlight.startTime}-${Date.now()}`;
        // 'pending' = waiting for a free slot in the client-side semaphore
        // (Cloud Run worker has only 5 concurrent slots; we cap at 3 to avoid
        // gateway 429s which arrive without CORS headers and surface as
        // confusing «Failed to fetch» errors in DevTools).
        this.downloadQueue.update(q => [...q, {
            id: jobId,
            highlight,
            status: 'pending',
            progress: 0
        }]);

        await this.acquireClipSlot();

        // Mark as processing once we actually start the network call.
        this.downloadQueue.update(q => q.map(j => j.id === jobId ? { ...j, status: 'processing', progress: 10 } : j));

        try {
            const baseName = `${(this.state.streamerName() || 'streamer').replace(/[^a-zA-Z0-9_.]/g, '_')}_${videoId}_clip_${this.formatTimeForFilename(highlight.clipStartTime)}`;
            const payload = {
                videoId: videoId,
                start: Math.floor(highlight.clipStartTime),
                end: Math.ceil(highlight.clipEndTime),
                name: baseName,
            };

            // Same retry pattern as downloadZipBatch: Cloud Run gateway 429s
            // arrive WITHOUT CORS headers (gateway rejects before our Express
            // middleware can stamp them), so the browser converts them into
            // generic «Failed to fetch» network errors. We catch those and
            // retry with exponential backoff up to MAX_RETRIES.
            const MAX_RETRIES = 8;
            const sendRequest = async (attempt: number = 0): Promise<Response> => {
                try {
                    const r = await fetch(`${cloudRunUrl}/download-clip`, {
                        method: 'POST',
                        headers: { ...cloudHeaders, 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    // 429 has two distinct meanings here:
                    //  - QUOTA_EXCEEDED → daily quota done; never retry; throw typed.
                    //  - everything else (Cloud Run gateway 429 from concurrency cap) → retryable.
                    if (r.status === 429) {
                        const cloned = r.clone();
                        const body = await cloned.json().catch(() => null);
                        if (body && body.error === 'QUOTA_EXCEEDED') {
                            throw new QuotaExceededError(
                                'clip',
                                Number(body.limit) || 0,
                                body.resetAt || null,
                            );
                        }
                        throw new Error('SERVER_BUSY');
                    }
                    if (r.status === 503) throw new Error('SERVER_BUSY');
                    return r;
                } catch (e: any) {
                    // Quota error is deterministic — bubble up immediately.
                    if (e instanceof QuotaExceededError) throw e;
                    if (attempt >= MAX_RETRIES) throw e;
                    const delay = Math.min(3000 * Math.pow(2, attempt), 30000); // 3s, 6s, 12s, 24s, 30s max
                    await new Promise(resolve => setTimeout(resolve, delay));
                    return sendRequest(attempt + 1);
                }
            };

            const response = await sendRequest();

            if (!response.ok) {
                const err = await response.json().catch(() => ({ error: response.statusText }));
                throw new Error(err.error || 'Server error');
            }

            const data = await response.json();

            this.downloadQueue.update(q => q.map(j => j.id === jobId ? { ...j, status: 'completed', progress: 100, downloadUrl: data.downloadUrl } : j));

            if (data.downloadUrl) {
                const cacheKey = this.cacheKeyFor(highlight, videoId);
                this.recentDownloads.update(m => {
                    const next = new Map(m);
                    next.set(cacheKey, {
                        url: data.downloadUrl,
                        filename: data.filename,
                        expiresAt: Date.now() + this.RECENT_DOWNLOAD_TTL_MS,
                    });
                    return next;
                });
                this.enqueueBrowserDownload(data.downloadUrl, data.filename);
            }

        } catch (error: any) {
            console.error('Download failed', error);
            this.downloadQueue.update(q => q.map(j => j.id === jobId ? { ...j, status: 'error', errorMessage: error.message } : j));
            setTimeout(() => {
                this.downloadQueue.update(q => q.filter(j => j.id !== jobId));
            }, 5000);
            // Re-throw quota errors so the caller (AppComponent.downloadClip) can
            // open the Pro upsell modal. Other errors stay swallowed — the queue
            // entry surfaces them in the UI.
            if (error instanceof QuotaExceededError) throw error;
        } finally {
            this.releaseClipSlot();
        }
    }

    /**
     * Sends multiple clips to backend to be zipped and returned as one file.
     */
    async downloadZipBatch(highlights: Highlight[], videoId: string | number): Promise<void> {
        if (this.demoService.isDemoMode()) {
            this.demoService.requestSignIn('download-zip');
            return;
        }

        // Daily-quota pre-check (Pro default is null/unlimited; admin can cap).
        const zq = this.quotaService.zipQuota();
        if (zq && zq.exhausted) {
            throw new QuotaExceededError(
                'zip',
                zq.limit ?? 0,
                this.quotaService.resetAt()?.toISOString() || null,
            );
        }

        const cloudRunUrl = this.firebaseService.cloudRunWorkerUrl;
        if (!cloudRunUrl) throw new Error('Cloud Run URL missing');
        if (highlights.length === 0) return;

        const cloudHeaders = await this.firebaseService.buildCloudRunHeaders();
        if (!cloudHeaders) {
            alert('Сначала войдите в аккаунт');
            return;
        }

        this.isBatchDownloading.set(true);
        this.batchDownloadProgress.set(0);
        this.batchDownloadQueued.set(false);

        try {
            const clipsPayload = highlights.map(h => ({
                start: Math.floor(h.clipStartTime),
                end: Math.ceil(h.clipEndTime),
                name: `${(this.state.streamerName() || 'streamer').replace(/[^a-zA-Z0-9_.]/g, '_')}_${videoId}_clip_${this.formatTimeForFilename(h.clipStartTime)}`
            }));

            // Generate jobId locally using Firestore
            const jobsColl = collection(this.firebaseService.firestore!, 'jobs');
            const jobRef = doc(jobsColl);
            const jobId = jobRef.id;

            // Tracks whether the worker has visibly picked the job up. Worker
            // currently runs backgroundJob() under `await`, so the POST handler
            // doesn't return until the ZIP finishes (5-10 min on a 4-clip batch).
            // Chrome's idle keepalive (~2-3 min) closes the connection first →
            // fetch throws "Failed to fetch". Without this flag the retry loop
            // below would POST the same jobId again and a fresh Worker instance
            // (maxScale=5) would restart backgroundJob from clip 1 — observed
            // in prod 2026-05-12 as «progress 12% → 0% → 80% → 0% …» on the same
            // jobId. Once we see status=processing|completed we stop retrying
            // and let onSnapshot drive the UI to completion.
            let serverPickedUp = false;

            // Start listening to the job BEFORE making the request
            const unsubscribe = onSnapshot(jobRef, (snapshot) => {
                const jobData = snapshot.data();
                if (!jobData) return;

                if (jobData.status === 'processing' || jobData.status === 'completed') {
                    serverPickedUp = true;
                }

                if (jobData.progress !== undefined) {
                    this.batchDownloadProgress.set(jobData.progress);
                    this.batchDownloadQueued.set(false); // Server started processing
                }

                if (jobData.status === 'completed') {
                    this.triggerBrowserDownload(jobData.resultUrl, jobData.filename || 'highlights.zip');
                    this.isBatchDownloading.set(false);
                    unsubscribe();
                } else if (jobData.status === 'error') {
                    alert(`Ошибка архивации: ${jobData.error}`);
                    this.isBatchDownloading.set(false);
                    unsubscribe();
                }
            }, (error) => {
                console.error("Firestore Error listening to job:", error);
                alert('Потеряно соединение с задачей архивации');
                this.isBatchDownloading.set(false);
            });

            // Send request with retry logic for HTTP 429 (Too Many Requests)
            // Note: Cloud Run generates 429 responses BEFORE reaching our Express app,
            // so they lack CORS headers. The browser converts them into network errors
            // ("Failed to fetch"), which we catch and retry below.
            const MAX_RETRIES = 10;
            const sendRequest = async (attempt: number = 0): Promise<void> => {
                try {
                    const response = await fetch(`${cloudRunUrl}/download-zip`, {
                        method: 'POST',
                        headers: { ...cloudHeaders, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            videoId: videoId,
                            clips: clipsPayload,
                            jobId: jobId
                        })
                    });

                    if (response.status === 429) {
                        // 429 has two meanings here (see /download-clip equivalent):
                        // QUOTA_EXCEEDED is deterministic until UTC midnight — surface
                        // immediately. Anything else = Cloud Run gateway concurrency 429
                        // (CORS-stripped) → retry.
                        const cloned = response.clone();
                        const body = await cloned.json().catch(() => null);
                        if (body && body.error === 'QUOTA_EXCEEDED') {
                            throw new QuotaExceededError(
                                'zip',
                                Number(body.limit) || 0,
                                body.resetAt || null,
                            );
                        }
                        throw new Error('SERVER_BUSY_429');
                    } else if (!response.ok) {
                        const err = await response.json().catch(() => ({ error: response.statusText }));
                        alert(`Ошибка сервера: ${err.error || response.statusText}`);
                        this.isBatchDownloading.set(false);
                        unsubscribe();
                    }
                    // On success (200), do nothing — Firestore listener handles the rest
                } catch (e: any) {
                    // Quota error stops the whole flow — no retry, surface to caller.
                    if (e instanceof QuotaExceededError) {
                        this.batchDownloadQueued.set(false);
                        this.isBatchDownloading.set(false);
                        unsubscribe();
                        throw e;
                    }
                    // If the worker already picked the job up (Firestore listener
                    // saw status=processing|completed), the network error here is
                    // just Chrome's idle keepalive closing the long-poll fetch —
                    // backgroundJob is still running on Cloud Run, and onSnapshot
                    // will drive the UI to completion. Retrying POST would only
                    // restart backgroundJob on a fresh instance and reset
                    // progress to 0 (the «12% → 0% → 80% → 0% …» bug).
                    if (serverPickedUp) {
                        console.log(`[zip] Worker already processing jobId=${jobId}; not retrying despite "${e?.message || e}"`);
                        this.batchDownloadQueued.set(false);
                        return;
                    }
                    // Network error (CORS-blocked 429) or browser timeout
                    if (attempt < MAX_RETRIES) {
                        this.batchDownloadQueued.set(true);
                        const delay = Math.min(5000 * Math.pow(2, attempt), 60000); // 5s, 10s, 20s, 40s, 60s max
                        console.log(`Server unavailable, retrying in ${delay / 1000}s... (attempt ${attempt + 1}/${MAX_RETRIES})`);
                        await new Promise(resolve => setTimeout(resolve, delay));
                        return sendRequest(attempt + 1);
                    } else {
                        this.batchDownloadQueued.set(false);
                        alert('Все серверы заняты. Попробуйте снова через минуту.');
                        this.isBatchDownloading.set(false);
                        unsubscribe();
                    }
                }
            };

            // Initial POST awaited so we can surface a 429 QUOTA_EXCEEDED before
            // the user thinks the job is in progress. Once 200 lands, Firestore
            // listener takes over for progress UI; the await resolves shortly.
            await sendRequest();

        } catch (e: any) {
            console.error('Batch download error:', e);
            this.isBatchDownloading.set(false);
            if (e instanceof QuotaExceededError) throw e;
            alert(`Ошибка при запросе на архивацию: ${e.message}`);
        }
    }

    private triggerBrowserDownload(url: string, filename: string) {
        // Backend signs the URL with responseDisposition=attachment, so GCS replies
        // with Content-Disposition: attachment and the browser saves the file
        // without navigating. target='_blank' is intentionally absent — it would
        // be treated as a popup (the click is async after fetch) and blocked.
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        // Defer the removal — Chrome occasionally cancels the navigation if
        // the anchor is removed before the GCS request actually goes out
        // (race seen with parallel downloads finishing close in time).
        setTimeout(() => {
            if (a.parentNode) a.parentNode.removeChild(a);
        }, 1000);
    }

    // Keep old interface for compatibility but unused
    isHighlightInQueueLegacy(startTime: number) { return false; }
    async startDownload(highlight: Highlight) { console.warn('Deprecated'); }

    private formatTimeForFilename(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${String(hours).padStart(2, '0')}-${String(minutes).padStart(2, '0')}-${String(seconds).padStart(2, '0')}`;
    }
}
